/**
 * Clue generation and triangulation for Signal Trace mode.
 * Pure functions — no side effects, no rendering.
 */

import type { PlanetState, ClueState, ClueHint } from '../types';
import { vecNormalize, vecSub } from '../math';

/**
 * Create empty clue state.
 */
export function initClueState(): ClueState {
  return {
    activatedPosts: {},
    clues: {},
    signalRevealed: {},
  };
}

/**
 * Generate a clue hint based on post location relative to the signal.
 */
export function generateClue(
  postPlanetIndex: number,
  signalPlanetIndex: number,
  planets: PlanetState[],
  activatedCount: number,
  _rng: { range: (min: number, max: number) => number }
): ClueHint {
  // First clue: simple "on this planet" or "not on this planet"
  if (activatedCount === 0) {
    if (postPlanetIndex === signalPlanetIndex) {
      return {
        type: 'notOnPlanet',
        planetIndex: postPlanetIndex,
        description: `Signal source IS on this planet (${postPlanetIndex}).`,
      };
    }
    return {
      type: 'notOnPlanet',
      planetIndex: postPlanetIndex,
      description: `Signal source is NOT on planet ${postPlanetIndex}.`,
    };
  }

  // Subsequent clues: directional hint toward signal planet
  const postPlanet = planets[postPlanetIndex];
  const signalPlanet = planets[signalPlanetIndex];
  if (!postPlanet || !signalPlanet) {
    return {
      type: 'directionHint',
      description: 'Signal data inconclusive.',
    };
  }

  const dir = vecNormalize(vecSub(signalPlanet.position, postPlanet.position));

  // Describe general direction
  let dirDesc = '';
  if (Math.abs(dir.x) > Math.abs(dir.y) && Math.abs(dir.x) > Math.abs(dir.z)) {
    dirDesc = dir.x > 0 ? 'positive-X' : 'negative-X';
  } else if (Math.abs(dir.y) > Math.abs(dir.z)) {
    dirDesc = dir.y > 0 ? 'above' : 'below';
  } else {
    dirDesc = dir.z > 0 ? 'positive-Z' : 'negative-Z';
  }

  return {
    type: 'directionHint',
    planetIndex: postPlanetIndex,
    description: `Signal bearing from planet ${postPlanetIndex}: ${dirDesc}.`,
  };
}

/**
 * Record that a team activated a listening post.
 */
export function addActivatedPost(
  clueState: ClueState,
  teamIndex: number,
  ruinId: number,
  postPlanetIndex: number,
  signalPlanetIndex: number,
  planets: PlanetState[],
  rng: { range: (min: number, max: number) => number }
): ClueState {
  const existingPosts = clueState.activatedPosts[teamIndex] ?? [];

  // Don't re-add duplicate
  if (existingPosts.includes(ruinId)) return clueState;

  const activatedCount = existingPosts.length;
  const clue = generateClue(postPlanetIndex, signalPlanetIndex, planets, activatedCount, rng);

  const existingClues = clueState.clues[teamIndex] ?? [];

  return {
    ...clueState,
    activatedPosts: {
      ...clueState.activatedPosts,
      [teamIndex]: [...existingPosts, ruinId],
    },
    clues: {
      ...clueState.clues,
      [teamIndex]: [...existingClues, clue],
    },
  };
}

/**
 * Check if a team has activated enough posts on DIFFERENT planets.
 */
export function checkTriangulation(
  clueState: ClueState,
  teamIndex: number,
  postsRequired: number,
  ruins: { id: number; planetIndex: number }[]
): boolean {
  const activatedIds = clueState.activatedPosts[teamIndex] ?? [];
  if (activatedIds.length < postsRequired) return false;

  // Check that posts are on different planets
  const planetSet = new Set<number>();
  for (const ruinId of activatedIds) {
    const ruin = ruins.find(r => r.id === ruinId);
    if (ruin) planetSet.add(ruin.planetIndex);
  }

  return planetSet.size >= postsRequired;
}

/**
 * Mark signal as revealed for a team.
 */
export function revealSignal(clueState: ClueState, teamIndex: number): ClueState {
  return {
    ...clueState,
    signalRevealed: {
      ...clueState.signalRevealed,
      [teamIndex]: true,
    },
  };
}

/**
 * Get all clues for a team.
 */
export function getTeamClues(clueState: ClueState, teamIndex: number): ClueHint[] {
  return clueState.clues[teamIndex] ?? [];
}
